import { useState, useEffect } from "react";

// Import reusable components
import FormInput from "@/components/formInputFields/FormInput";
import PriceInput from "@/components/formInputFields/FormPricingInput";

// Import inhouse icons
import { X } from "@/components/icons_new";

export default function VariantEditModal({ variant, onClose, onSave }) {
    const [formState, setFormState] = useState({
      sku: "",
      barcode: "",
      price: "",
      compareAtPrice: "",
      costPerItem: "",
      inventoryQuantity: "",
      trackInventory: true,
    });

    // Inventory formatting helper (kept simple: digits only -> with commas)
    const formatInventoryWithCommas = (raw) => {
      if (raw == null || raw === "") return "";
      const digits = String(raw).replace(/\D/g, "");
      if (digits === "") return "";
      const n = parseInt(digits, 10);
      if (isNaN(n)) return "";
      return n.toLocaleString();
    };

    useEffect(() => {
      if (variant) {
        setFormState({
          sku: variant.sku || "",
          barcode: variant.barcode || "",
          price: variant.price ?? "",
          compareAtPrice: variant.compareAtPrice ?? "",
          costPerItem: variant.costPerItem ?? "",
          inventoryQuantity: (variant.trackInventory ? formatInventoryWithCommas(variant.inventoryQuantity ?? "") : ""),
          trackInventory: variant.trackInventory ?? true,
        });
      }
    }, [variant]);

    const handleChange = (field, value) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
    };

    // SKU sanitization: trim whitespace (leading, trailing, internal), allow safe chars, limit to 30 chars
    const sanitizeSku = (raw) => {
      if (raw == null) return "";
      const noSpaces = String(raw).replace(/\s+/g, "");
      // allow letters, numbers, dash, underscore, dot
      const safe = noSpaces.replace(/[^A-Za-z0-9_.-]/g, "");
      return safe.slice(0, 30);
    };
    const handleSkuChange = (e) => {
      const next = sanitizeSku(e.target.value);
      setFormState((prev) => ({ ...prev, sku: next }));
    };

    // Barcode: trim only leading/trailing whitespace
    const handleBarcodeChange = (e) => {
      const digitsOnly = String(e.target.value ?? "").replace(/\D/g, "");
      setFormState((prev) => ({ ...prev, barcode: digitsOnly }));
    };
    const handleBarcodeBlur = () => {
      setFormState((prev) => ({ ...prev, barcode: (prev.barcode ?? "").trim() }));
    };

    // Money input sanitization helpers
    const sanitizeMoneyOnChange = (raw) => {
      if (raw == null) return "";
      let cleaned = String(raw).replace(/[^0-9.]/g, "");
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        const intPart = cleaned.slice(0, firstDot);
        let fracPart = cleaned.slice(firstDot + 1).replace(/\./g, "");
        fracPart = fracPart.slice(0, 2);
        const intNoLead = intPart.replace(/^0+(?=\d)/, "");
        cleaned = `${intNoLead === "" ? "0" : intNoLead}.${fracPart}`;
      } else {
        cleaned = cleaned.replace(/^0+(?=\d)/, "");
        cleaned = cleaned === "" ? "" : cleaned;
      }
      return cleaned;
    };

    const finalizeMoneyOnBlur = (raw) => {
      if (raw == null || raw === "") return "";
      let cleaned = String(raw).replace(/[^0-9.]/g, "");
      // Keep only first dot
      const firstDot = cleaned.indexOf(".");
      if (firstDot !== -1) {
        cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "");
      }
      const num = parseFloat(cleaned);
      if (isNaN(num)) return "";
      // Format to two decimals
      const fixed = num.toFixed(2);
      // Insert thousands separators for readability
      const [intPart, decPart] = fixed.split(".");
      const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${withCommas}.${decPart}`;
    };

    const handleMoneyChange = (field) => (e) => {
      const next = sanitizeMoneyOnChange(e.target.value);
      setFormState((prev) => ({ ...prev, [field]: next }));
    };

    const handleMoneyBlur = (field) => () => {
      setFormState((prev) => ({ ...prev, [field]: finalizeMoneyOnBlur(prev[field]) }));
    };

    // Inventory sanitization: digits only, no leading zeros (except single 0)
    const sanitizeInventoryOnChange = (raw) => {
      if (raw == null) return "";
      const digits = String(raw).replace(/\D/g, "");
      if (digits === "") return "";
      const trimmed = digits.replace(/^0+/, "");
      return trimmed === "" ? "0" : trimmed;
    };
    const handleInventoryChange = (e) => {
      const next = sanitizeInventoryOnChange(e.target.value);
      setFormState((prev) => ({ ...prev, inventoryQuantity: next }));
    };
    const handleInventoryBlur = () => {
      setFormState((prev) => ({
        ...prev,
        inventoryQuantity: formatInventoryWithCommas(prev.inventoryQuantity),
      }));
    };

    const handleSave = () => {
      // Compute updated meta dirty flags when values changed
      const prev = variant || {};
      const prevMeta = prev.meta || {};
      const stripCommas = (v) => (typeof v === 'string' ? v.replace(/,/g, "") : v);
      const toNumber = (v) => (v === "" || v === null || typeof v === "undefined" ? "" : Number(stripCommas(v)));

      const newMeta = {
        ...prevMeta,
        priceDirty:
          toNumber(prev.price) !== toNumber(formState.price) ? true : prevMeta.priceDirty,
        compareAtDirty:
          toNumber(prev.compareAtPrice) !== toNumber(formState.compareAtPrice)
            ? true
            : prevMeta.compareAtDirty,
        costDirty:
          toNumber(prev.costPerItem) !== toNumber(formState.costPerItem) ? true : prevMeta.costDirty,
        inventoryDirty:
          toNumber(prev.inventoryQuantity) !== toNumber(formState.inventoryQuantity)
            ? true
            : prevMeta.inventoryDirty,
        skuDirty: prev.sku !== formState.sku ? true : prevMeta.skuDirty,
        barcodeDirty: prev.barcode !== formState.barcode ? true : prevMeta.barcodeDirty,
        trackInvDirty:
          Boolean(prev.trackInventory) !== Boolean(formState.trackInventory)
            ? true
            : prevMeta.trackInvDirty,
      };
      // Normalize numeric fields so the table shows updates immediately
      const price = toNumber(formState.price);
      const compareAtPrice = toNumber(formState.compareAtPrice);
      const costPerItem = toNumber(formState.costPerItem);
      const inventoryQuantity = formState.trackInventory
        ? (formState.inventoryQuantity === "" ? "" : parseInt(String(formState.inventoryQuantity).replace(/,/g, "")) || 0)
        : ""; // not tracked -> no numeric quantity

      onSave({
        ...variant,
        ...formState,
        price,
        compareAtPrice,
        costPerItem,
        inventoryQuantity,
        meta: newMeta,
      });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000]/45 backdrop-blur-sm">
          <div className="w-[900px] flex flex-col bg-white rounded-lg relative" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex text-[#000000] items-center justify-between bg-[#ececec] py-4 px-6 rounded-t-lg">
              <span className="font-medium">
                Edit {variant?.subVariantName || variant?.options?.Size}
              </span>
              <X className="h-5 w-5 cursor-pointer" onClick={onClose}/>
            </div>

            {/* Form fields */}
            <div className="p-6">
                <div className="flex gap-[1em]">
                  <PriceInput
                    className="w-[50%]"
                    name="compareAtPrice"
                    label="Price"
                    value={formState.compareAtPrice}
                    onChange={handleMoneyChange("compareAtPrice")}
                    onBlur={handleMoneyBlur("compareAtPrice")}
                  />
                  <PriceInput
                    className="w-[50%]"
                    name="price"
                    label="Discounted price"
                    value={formState.price}
                    onChange={handleMoneyChange("price")}
                    onBlur={handleMoneyBlur("price")}
                  />
                </div>

                <div className="mt-4 grid grid-cols-3 gap-[1em]">
                  <PriceInput
                    className="w-full"
                    name="costPerItem"
                    label="Cost per item"
                    value={formState.costPerItem}
                    onChange={handleMoneyChange("costPerItem")}
                    onBlur={handleMoneyBlur("costPerItem")}
                  />
                  <ReadOnlyMoney label="Profit" price={formState.price} cost={formState.costPerItem} />
                  <ReadOnlyPercent label="Margin" price={formState.price} cost={formState.costPerItem} />
                </div>

                <div className="border border-[#ededed] mb-[1em]"></div>

                <div className="flex gap-[1em]">
                  <FormInput
                    className="w-[50%]"
                    name="sku"
                    label="SKU"
                    value={formState.sku}
                    onChange={handleSkuChange}
                  />

                  <FormInput
                    className="w-[50%]"
                    name="barcode"
                    label="Barcode"
                    value={formState.barcode}
                    onChange={handleBarcodeChange}
                    onBlur={handleBarcodeBlur}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </div>

                <div className="border border-[#ededed] mb-[1em]"></div>

                <div className="flex flex-col gap-[1em] mt-4">
                  
                  <div className="w-[50%] flex items-end">
                    <div className="flex items-center gap-2 w-full">
                      <input
                        id="trackInventory"
                        type="checkbox"
                        className="accent-[#000]"
                        checked={!!formState.trackInventory}
                        onChange={(e) => handleChange("trackInventory", e.target.checked)}
                      />
                      <label htmlFor="trackInventory" className="text-sm text-[#333] font-medium">
                        Track quantity
                      </label>
                    </div>
                  </div>
                  {formState.trackInventory ? (
                    <FormInput
                      className="w-[50%]"
                      name="inventory"
                      label="Inventory"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      min="0"
                      placeholder="0"
                      value={formState.inventoryQuantity}
                      onChange={handleInventoryChange}
                      onBlur={handleInventoryBlur}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  ) : (
                    <div className="w-[50%]">
                      <label className="block text-sm font-medium text-[#636363] mb-1">Inventory</label>
                      <input
                        className="w-full mb-6 rounded-md border border-[#CACACA] px-4 py-2 bg-[#F6F6F6] text-[#777] text-sm cursor-not-allowed"
                        value="Not tracked"
                        disabled
                        readOnly
                      />
                    </div>
                  )}
                </div>
            </div>

            {/* Save and cancel */}
            <div className="flex justify-end gap-2 py-3 px-5 border-t border-[#d2d2d2]">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-[#000] bg-white border border-[#9e9e9e] rounded-[10px] hover:bg-[#f3f3f3]"
              >
                Cancel
              </button>

              <button
                onClick={handleSave}
                className={`px-6 py-2 text-sm font-medium text-white rounded-[10px] transition
                  bg-[#000] hover:bg-[#414141]
                  disabled:bg-[#c9c9c9] disabled:text-[#555] disabled:cursor-not-allowed disabled:hover:bg-[#c9c9c9]
                `}
              >
                Save
              </button>
            </div>
          </div>
        </div>
    );
}

function formatMoney(n) {
  if (n === null || n === undefined || n === "") return "";
  const v = Number(String(n).replace(/,/g, ""));
  if (isNaN(v)) return "";
  const [intPart, decPart = "00"] = v.toFixed(2).split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${withCommas}.${decPart}`;
}

function ReadOnlyMoney({ label, price, cost }) {
  const p = Number(String(price || "").replace(/,/g, ""));
  const c = Number(String(cost || "").replace(/,/g, ""));
  const profit = isNaN(p) || isNaN(c) ? "" : formatMoney(p - c);
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-[#636363] mb-1">{label}</label>
      <div className="flex items-center rounded-md border border-[#CACACA] bg-[#F6F6F6] px-3 py-2 text-sm w-full opacity-80">
        <span className="text-[#000] mr-2 font-medium select-none">KES</span>
        <input className="bg-[#F6F6F6] w-full focus:outline-none text-[#333]" value={profit} readOnly />
      </div>
    </div>
  );
}

function ReadOnlyPercent({ label, price, cost }) {
  const p = Number(String(price || "").replace(/,/g, ""));
  const c = Number(String(cost || "").replace(/,/g, ""));
  const margin = isNaN(p) || isNaN(c) || p === 0 ? "" : `${(((p - c) / p) * 100).toFixed(1)}%`;
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-[#636363] mb-1">{label}</label>
      <input className="w-full rounded-md border border-[#CACACA] px-4 py-2 bg-[#F6F6F6] text-[#333] text-sm" value={margin} placeholder="0%" readOnly />
    </div>
  );
}
